import { action, observe, runInAction, makeObservable, observable, computed, $mobx, configure } from 'mobx';
import 'react';
import { deepObserve } from 'mobx-utils';
import debounce from 'lodash.debounce';
import { validate, getMetadataStorage } from 'class-validator';
import { uniq, splitByPoint, path } from '@worksolutions/utils';

function createSetter(fieldName) {
    return action((value)=>{
        if (this[fieldName] === value) return;
        this[fieldName] = value;
        this.setError?.(fieldName, undefined);
    });
}

function createSetterArrayObservable(fieldName) {
    return action((value)=>{
        const original = this[fieldName];
        if (original === value) return;
        original.splice(0, original.length, ...value);
        this.setError?.(fieldName, undefined);
    });
}

function createSetterEvent(fieldName) {
    const setter = this.createSetter(fieldName);
    return action((event)=>setter(event.target.value));
}

function createToggle(fieldName) {
    return action(()=>{
        this[fieldName] = !this[fieldName];
        this.setError?.(fieldName, undefined);
    });
}

function createPush(fieldName) {
    return action((value)=>{
        this[fieldName].push(value);
        this.setError?.(fieldName, undefined);
    });
}

function createPushArray(fieldName) {
    return action((value)=>{
        this[fieldName].push(...value);
        this.setError?.(fieldName, undefined);
    });
}

function createDeleteByValue(fieldName) {
    return action((value)=>{
        const index = this[fieldName].indexOf(value);
        if (index === -1) return;
        this[fieldName].splice(index, 1);
    });
}

function createDeleteByIndex(fieldName) {
    return action((index)=>{
        this[fieldName].splice(index, 1);
    });
}

function createUpdateByIndex(fieldName) {
    return action((index, value)=>{
        this[fieldName][index] = value;
        this.setError?.(fieldName, undefined);
    });
}

function createReoder(fieldName, idFieldName) {
    return action((newOrder)=>{
        const field = this[fieldName];
        this[fieldName] = newOrder.map((id)=>field.find((fieldValue)=>fieldValue[idFieldName] === id));
        this.setError?.(fieldName, undefined);
    });
}

function registerOnFieldChangeCallback(callback, fieldName, deep = false, certainField = false) {
    if (deep) return deepObserve(this[fieldName], callback);
    if (certainField) return observe(this[fieldName], callback);
    return observe(this, fieldName, callback);
}
function createDisposableDebouncedCallback(callback, debounceTime) {
    let disposed = false;
    const debouncedCallback = debounce((...args)=>!disposed && callback(...args), debounceTime);
    return {
        debouncedCallback,
        disposeCallback: ()=>void (disposed = true)
    };
}
function onFieldChange(callback, fieldName, debounceTime, deep, certainField) {
    const { disposeCallback, debouncedCallback } = createDisposableDebouncedCallback(callback, debounceTime);
    const dispose = registerOnFieldChangeCallback.call(this, debouncedCallback, fieldName, deep, certainField);
    return ()=>{
        disposeCallback();
        dispose();
    };
}
function onMultipleFieldChange(callback, fieldNames, debounceTime, deep, certainField) {
    const { disposeCallback, debouncedCallback } = createDisposableDebouncedCallback(callback, debounceTime);
    const disposers = fieldNames.map((fieldName)=>registerOnFieldChangeCallback.call(this, debouncedCallback, fieldName, deep, certainField));
    return ()=>{
        disposeCallback();
        disposers.forEach((dispose)=>dispose());
    };
}

function validateAsync({ stopAtFirstError = false, groups } = {}) {
    return validate(this, {
        stopAtFirstError,
        groups,
        forbidUnknownValues: false
    });
}
async function validateAsyncAndThrowFirstError(validateOptions) {
    const [error] = await validateAsync.call(this, {
        ...validateOptions,
        stopAtFirstError: true
    });
    // class-validator ValidationError — не Error: без stack, поэтому в лог/otel-ошибку не попадал трейс.
    // Бросаем настоящий Error (stack захватывается здесь, на месте падения валидации).
    if (error) {
        const thrown = new Error(error.toString(false, false, "", true));
        thrown.name = "ValidationError";
        throw thrown;
    }
}
function subscribeFullValid() {
    const validator = async ()=>{
        const [error] = await this.validateAsync({
            stopAtFirstError: true
        });
        runInAction(()=>this.fullValid = !error);
    };
    observe(this, debounce(validator, 1));
}
function getValidationGroupFields(validationGroups, strict = false) {
    const validators = getMetadataStorage().getTargetValidationMetadatas(// @ts-ignore
    this.__proto__.constructor, undefined, false, strict, validationGroups);
    return uniq(validators.map((validator)=>validator.propertyName));
}
function hasValidationGroupError(validationGroup) {
    const fields = getValidationGroupFields.call(this, [
        validationGroup
    ]);
    return !!fields.find((field)=>!!this.errors[field]);
}
function getHasAnyUnresolvedErrors() {
    return !!Object.values(this.errors).find((error)=>error);
}

function getViewErrors() {
    const submitted = this.submitted;
    const forceShowErrorKeys = this.forceShowErrorKeys;
    return Object.fromEntries(Object.entries(this.errors).map(([key, value])=>submitted || forceShowErrorKeys[key] ? [
            key,
            value
        ] : null).filter((error)=>error));
}
function setError(fieldName, error, forceShow = false) {
    const fieldNameArray = splitByPoint(fieldName);
    const prePathEntity = path(fieldNameArray.slice(0, -1), this);
    const resultFieldName = fieldNameArray[fieldNameArray.length - 1];
    if (forceShow) prePathEntity.forceShowErrorKeys[resultFieldName] = true;
    prePathEntity.errors[resultFieldName] = error;
}
function removeError(fieldName) {
    this.errors[fieldName] = undefined;
}

function applyErrors(target, errors) {
    for (const error of errors){
        const mainErrorField = error.property;
        if (error.constraints) target.setError(mainErrorField, error.constraints[Object.keys(error.constraints)[0]]);
        else target.setError(mainErrorField, "_");
    }
}
async function validateAndSetErrors(options = {}) {
    const validationErrors = await validateAsync.call(this, options);
    if (validationErrors.length === 0) return {
        valid: true
    };
    applyErrors(this, validationErrors);
    return {
        valid: false,
        errors: validationErrors
    };
}
function getAllSubEntities() {
    const subEntities = [];
    Object.values(this).forEach((value)=>{
        if (BaseEntity.isBaseEntity(value)) subEntities.push(value);
        if (Array.isArray(value)) {
            value.forEach((value)=>{
                if (BaseEntity.isBaseEntity(value)) subEntities.push(value);
            });
        }
    });
    subEntities.forEach((entity)=>subEntities.push(...getAllSubEntities.call(entity)));
    return subEntities;
}
async function submitEntity(options) {
    this.forceShowErrorKeys = {};
    this.submitted = true;
    const result = await validateAndSetErrors.call(this, options);
    this.lastSubmitValid = result.valid;
    return result;
}
//TODO: вызывать сабмиты только для тех сущностей у которых есть @ValidateNested
async function submit(options) {
    const entities = [
        this,
        ...getAllSubEntities.call(this)
    ];
    const results = await Promise.all(entities.map((entity)=>submitEntity.call(entity, options)));
    return !results.some((result)=>!result.valid);
}

function createMobXAnnotations() {
    const donor = observable({
        errors: {},
        submitted: false,
        forceShowErrorKeys: {},
        fullValid: false,
        lastSubmitValid: false,
        setError: setError.bind(this),
        removeError: removeError.bind(this),
        submit: submit.bind(this)
    }, {
        errors: observable,
        submitted: observable,
        forceShowErrorKeys: observable,
        fullValid: observable,
        lastSubmitValid: observable,
        setError: action,
        removeError: action,
        submit: action
    });
    Object.assign(this, donor);
    this.setError = donor.setError;
    this.removeError = donor.removeError;
    this.submit = donor.submit;
    const viewErrorsComputed = computed(()=>getViewErrors.call(this));
    const hasAnyUnresolvedErrorsComputed = computed(()=>getHasAnyUnresolvedErrors.call(this));
    Object.defineProperties(this, {
        viewErrors: {
            get: ()=>viewErrorsComputed.get()
        },
        hasAnyUnresolvedErrors: {
            get: ()=>hasAnyUnresolvedErrorsComputed.get()
        }
    });
}
function initMobx({ canValidate = false } = {}) {
    makeObservable(this);
    if (canValidate) {
        createMobXAnnotations.call(this);
        subscribeFullValid.call(this);
    }
}

configure({
    isolateGlobalState: false,
    enforceActions: "always",
    computedRequiresReaction: false,
    reactionRequiresObservable: false
});
class BaseEntity {
    static isBaseEntity(value) {
        return value instanceof BaseEntity;
    }
    mobx(...args) {
        initMobx.apply(this, args);
    }
    createSetter(...args) {
        return createSetter.apply(this, args);
    }
    createSetterArrayObservable(...args) {
        return createSetterArrayObservable.apply(this, args);
    }
    createSetterEvent(...args) {
        return createSetterEvent.apply(this, args);
    }
    createToggle(...args) {
        return createToggle.apply(this, args);
    }
    createPush(...args) {
        return createPush.apply(this, args);
    }
    createPushArray(...args) {
        return createPushArray.apply(this, args);
    }
    createDeleteByValue(...args) {
        return createDeleteByValue.apply(this, args);
    }
    createDeleteByIndex(...args) {
        return createDeleteByIndex.apply(this, args);
    }
    createUpdateByIndex(...args) {
        return createUpdateByIndex.apply(this, args);
    }
    createReorder(...args) {
        return createReoder.apply(this, args);
    }
    validateAsync(...args) {
        return validateAsync.apply(this, args);
    }
    validateAsyncAndThrowFirstError(...args) {
        return validateAsyncAndThrowFirstError.apply(this, args);
    }
    getValidationGroupFields(...args) {
        return getValidationGroupFields.apply(this, args);
    }
    hasValidationGroupError(...args) {
        return hasValidationGroupError.apply(this, args);
    }
    onFieldChange(...args) {
        return onFieldChange.apply(this, args);
    }
    onMultipleFieldChange(...args) {
        return onMultipleFieldChange.apply(this, args);
    }
    emitArrayLengthChangedEmptyEventHack(fieldName) {
        const field = this[fieldName][$mobx];
        field.notifyArraySplice_(-1, [], []);
    }
    disableEnumerableForMobx(fieldName) {
        Object.defineProperty(this, fieldName, {
            enumerable: false
        });
    }
}

export { BaseEntity as B };
//# sourceMappingURL=BaseEntity_chunkExport-DT5ZCZ6j.mjs.map
